import {useEffect} from 'react';

import classNames from 'classnames';

import {Illustration} from 'shells/illustrations/illustration';
import {TwoPaneModal} from 'shells/modal/two-pane-modal';

import {AiFeaturePeriod, LimitedType} from 'nutshell-graphql-types';
import type {AiFeatureType} from 'nutshell-graphql-types';

import {analyticsClient} from '../../../analytics-client';

import {useGetSessionUser} from '../../core/use-get-session-user';
import {useGetLimitsByType} from '../../limits/use-get-limits-by-type';

import {useGetAiFeature} from '../graphql/hooks/use-get-ai-feature';

import {NotInPlanContent} from './not-in-plan-content';
import {OutOfCreditsContent} from './out-of-credits-content';

import styles from './ai-feature-gate-modal.module.css';

export type AiFeatureGateReason = 'outOfCredits' | 'notInPlan';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    reason: AiFeatureGateReason;
    featureName?: string;
    aiFeatureType?: AiFeatureType;
}

export function AiFeatureGateModal(props: Props) {
    const {user} = useGetSessionUser();
    const {limits} = useGetLimitsByType(LimitedType.AI_OUTCOMES);

    const shouldFetchFeature =
        props.isOpen && props.reason === 'notInPlan' && Boolean(props.aiFeatureType);
    const {aiFeature, isLoading: isLoadingAiFeature} = useGetAiFeature(
        props.aiFeatureType as AiFeatureType,
        AiFeaturePeriod.MONTH,
        !shouldFetchFeature
    );
    const minimumPlanName: string | undefined = aiFeature?.minimumPlanName ?? undefined;

    const isAdmin = Boolean(user && user.permissions.canAccessBilling);

    useEffect(() => {
        if (props.isOpen && user) {
            analyticsClient.track(analyticsClient.EVENTS.TRACK_OUT_OF_OUTCOMES_MODAL_VIEWED, {
                reason: props.reason,
                aiFeatureType: props.aiFeatureType,
                isAdmin: isAdmin,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen, user]);

    if (!user) {
        return null;
    }

    const leftPaneClass = classNames(
        'flex justify-center align-center full-width full-height overflow-hidden',
        {
            [styles['left-pane--credits']]: props.reason === 'outOfCredits',
            [styles['left-pane--upgrade']]: props.reason === 'notInPlan',
        }
    );

    const illustration =
        props.reason === 'outOfCredits' ? (
            <Illustration src='ai-homepage/ai-welcome-carousel' />
        ) : (
            <Illustration src='upgrade-your-plan' width={240} />
        );

    const rightContent =
        props.reason === 'outOfCredits' ? (
            <OutOfCreditsContent isAdmin={isAdmin} limits={limits} onClose={props.onClose} />
        ) : (
            <NotInPlanContent
                isAdmin={isAdmin}
                featureName={props.featureName}
                limits={limits}
                onClose={props.onClose}
                minimumPlanName={minimumPlanName}
                isLoadingAiFeature={isLoadingAiFeature && !minimumPlanName}
            />
        );

    return (
        <TwoPaneModal
            // @ts-expect-error - Modal accepts isOpen via spread
            isOpen={props.isOpen}
            onClose={props.onClose}
            maxHeight={700}
            closeButton={false}
            leftSection={<div className={leftPaneClass}>{illustration}</div>}
            rightSection={rightContent}
        />
    );
}
